import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { Tolgee, DevTools, TolgeeProvider } from "@tolgee/react";
import { FormatIcu } from '@tolgee/format-icu';
import LocalStorage from '../Helpers/LocalStorage';
import { INTERFACE_TRANSLATION_LANGUAGES } from '../Constants/constantValues';

const TolgeeContext = createContext();

const getValidatedLanguage = () => {
    const storedLanguage = LocalStorage.getInterfaceLanguage();
    
    // If user has stored language preference, use it
    if (storedLanguage && INTERFACE_TRANSLATION_LANGUAGES.some(lang => lang.locale === storedLanguage)) {
        return storedLanguage;
    }
    
    // Check browser language preference
    const browserLanguage = navigator.language || navigator.userLanguage;
    if (browserLanguage && INTERFACE_TRANSLATION_LANGUAGES.some(lang => lang.locale === browserLanguage)) {
        return browserLanguage;
    }
    
    // Check browser language without region (e.g., 'en' from 'en-GB')
    const browserLanguageShort = browserLanguage ? browserLanguage.split('-')[0] : null;
    if (browserLanguageShort) {
        const matchingLanguage = INTERFACE_TRANSLATION_LANGUAGES.find(lang => 
            lang.locale.split('-')[0] === browserLanguageShort
        );
        if (matchingLanguage) {
            return matchingLanguage.locale;
        }
    }
    return 'en-US';
};

// Tolgee configuration
const tolgee = Tolgee()
    .use(DevTools())
    .use(FormatIcu())
    .init({
        language: getValidatedLanguage(),
        // for development
        // apiUrl: process.env.VITE_APP_TOLGEE_API_URL,
        // apiKey: process.env.VITE_APP_TOLGEE_API_KEY,
        staticData: INTERFACE_TRANSLATION_LANGUAGES.reduce((acc, lang) => {
            acc[lang.locale] = async () => {
                try {
                    return await import(`../Localization/${lang.locale}.json`);
                } catch (error) {                    
                    return await import(`../Localization/en-GB.json`);;
                }
            };
            return acc;
        }, {}),
    });

// Tolgee Context Provider component
const TolgeeContextProvider = ({ children }) => {
    const changeLanguage = useCallback((languageCode) => {
        tolgee.changeLanguage(languageCode);
    }, []);

    const contextValue = useMemo(() => ({
        tolgee,
        changeLanguage,
    }), [changeLanguage]);

    return (
        <TolgeeContext.Provider value={contextValue}>
            <TolgeeProvider
                tolgee={tolgee}
                fallback="Loading..."
            >
                {children}
            </TolgeeProvider>
        </TolgeeContext.Provider>
    );
};

export default TolgeeContextProvider;
